import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {Redirect, useHistory} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import {pdf} from '@react-pdf/renderer';
import PageHeader from './PageHeader';
import SettingsPanel from './SettingsPanel';
import PreviewPane from './PreviewPane';
import QuotePDF from './pdf/QuotePDF';
import {buildDefaultQuoteTemplate, EMPTY_QUOTE_TEMPLATE} from './config/defaultTemplate';
import {MERGE_FIELDS} from './config/mergeFields';
import {resolveTemplate} from './resolver/resolveTemplate';
import {resolveDownloadFileName} from './resolver/resolveTemplateString';
import type {MergeContext, QuoteTemplate} from './resolver/types';
import {buildQuoteDocumentData} from './quoteDocument';
import {prepareQuotePublish, PublishBlockedReason} from './publishQuote';
import {showPublishQuoteConfirmationModal} from './PublishQuoteConfirmationModal';
import CreateQuoteMutation from '../../../mutations/create_quote_mutation';
import Util from '../../../forecast-app/shared/util/util';
import ProjectUtil from '../../../forecast-app/shared/util/ProjectUtil';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {isQuotesEnabled} from '../../../forecast-app/shared/util/FeatureUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {projectUrl} from '../../../directApi';
import {getProjectIndicatorString} from '../../../forecast-app/shared/components/project-indicator/support/ProjectIndicatorLogic';
import {useSecondaryNavState} from '../../../forecast-app/navigation/secondary/SecondaryNavigationStateManager';
import {useTrackPage} from '../../../tracking/amplitude/hooks/useTrackPage';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import {isLiveQuote} from '../quote-activity/quoteActivityCopy';

/* The project routes render page content beside the secondary nav in a flex row; without an
   explicit flex-grow the designer only spans its content's intrinsic width. */
const Page = styled.div`
	flex: 1;
	min-width: 0;
`;
const Layout = styled.div`
	display: grid;
	grid-template-columns: 1fr 2fr;
	gap: 24px;
	padding: 24px;
	height: calc(100vh - 64px);
`;
const SettingsCol = styled.div`
	/* Grid items default to min-height auto, which lets content stretch the track — the
	   explicit floor is what makes the column scroll instead of growing past the pane. */
	min-height: 0;
	overflow-y: auto;
`;
const PreviewCol = styled.div`
	background: #f8f8f8;
	display: flex;
	align-items: stretch;
	min-height: 0;
	overflow: hidden;
`;
const ErrorBanner = styled.div`
	margin: 16px 24px 0 24px;
	padding: 12px 16px;
	border: 1px solid #c00;
	border-radius: 4px;
	color: #c00;
	font-size: 13px;
`;

/** The slice of the publish response this page reads back — see create_quote_mutation. */
type PublishResponse = {
	createQuote?: {project?: {quote?: {revisionNumber?: number | null} | null} | null} | null;
};

type Props = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Relay-supplied; shape enforced by the QuoteDesignerPage_viewer fragment
	viewer: any;
	projectId?: string;
};

export const QuoteDesignerPageInner: React.FC<Props> = ({viewer}) => {
	const intl = useIntl();
	const history = useHistory();
	useTrackPage('Quote Designer');

	const project = viewer.project;
	const company = viewer.company;
	const baselineUrl = project ? `${projectUrl(project.companyProjectId, project.customProjectId)}/baseline` : '/';

	const existingQuote = project?.quote ?? null;
	// A REVISING quote is NOT "already built": project.quote returns the latest row by revision
	// regardless of status, so STATUS — not existence — decides whether this route is a create or a
	// bounce. The same isLiveQuote the Baseline's two CTA gates use.
	const isViewMode = isLiveQuote(existingQuote?.status);
	const liveData = useMemo(
		() =>
			project && company && !isViewMode
				? buildQuoteDocumentData(project, company, {
						deliverablesOn: ProjectUtil.deliverablesUiEnabled(project),
						// The version THIS publish will file, not the one on screen before it. Passing
						// nothing leaves buildQuoteDocumentData on its `?? 1` default, which was right
						// only while revision 1 was the sole revision that could exist - every later
						// revision then reads v1 in the settings panel, the preview AND the generated
						// PDF's cover page, which is stored and customer-facing.
						//
						// project.quote is the latest row by revision whatever its status, so after a
						// revise it is the REVISING row and the next number is its + 1; with no quote at
						// all this is revision 1. It predicts what the server will assign rather than
						// reading it, because the PDF is built here and posted WITH the publish.
						revision: (existingQuote?.revisionNumber ?? 0) + 1,
				  })
				: null,
		[project, company, isViewMode, existingQuote]
	);

	const [template, setTemplate] = useState<QuoteTemplate>(() =>
		liveData ? buildDefaultQuoteTemplate(intl, liveData) : EMPTY_QUOTE_TEMPLATE
	);
	const [publishing, setPublishing] = useState(false);
	const [publishBlocked, setPublishBlocked] = useState<PublishBlockedReason | null>(null);

	const mergeContext: MergeContext | null = useMemo(() => (liveData ? {data: liveData, intl} : null), [liveData, intl]);

	// The designer needs the full width, and an open project nav invites navigating away
	// mid-edit — collapse it while this page is mounted and restore it on leave. The ref keeps
	// the effect mount-only: the provider recreates its callbacks every render, and collapsing
	// flips `expanded`, so depending on either would re-run the effect against its own change.
	const navState = useSecondaryNavState();
	const navStateRef = useRef(navState);
	navStateRef.current = navState;
	useEffect(() => {
		const nav = navStateRef.current;
		if (!nav?.expanded || !nav.forceCollapse) return undefined;
		nav.forceCollapse();
		return () => navStateRef.current?.forceExpand?.();
	}, []);

	const handleBack = useCallback(() => {
		history.push(baselineUrl);
	}, [history, baselineUrl]);

	const handleDownload = useCallback(async () => {
		if (!liveData) return;
		const resolved = resolveTemplate(template, {data: liveData, intl});
		const blob = await pdf(<QuotePDF data={liveData} template={resolved} intl={intl} />).toBlob();
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = resolveDownloadFileName(resolved.fileNameTemplate, {data: liveData, intl});
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		// Deferred: Safari (and some Firefox versions) abort the download when the blob URL is
		// revoked in the same tick as the click.
		setTimeout(() => URL.revokeObjectURL(url), 0);
	}, [liveData, template, intl]);

	const doPublish = useCallback(async () => {
		if (!liveData || !project) return;
		const resolved = resolveTemplate(template, {data: liveData, intl});
		const blob = await pdf(<QuotePDF data={liveData} template={resolved} intl={intl} />).toBlob();
		// The uploaded file keeps the served naming convention (the company service names the filed
		// row itself); the Export file name only names local downloads.
		const prepared = prepareQuotePublish(blob, `Quote-${liveData.projectIndicator}-v1.pdf`);
		if (!prepared.ok) {
			setPublishBlocked(prepared.reason);
			return;
		}
		setPublishBlocked(null);
		setPublishing(true);
		Util.CommitMutation(
			CreateQuoteMutation,
			{projectId: project.id, expiryDate: liveData.expiryDate},
			(response: PublishResponse) => {
				// The revision comes from the row the server just wrote, not a literal: once a
				// revise flow can publish a second quote, a hard-coded 1 would report every
				// revision as the first and silently flatten the revision-volume metric. Left
				// absent rather than defaulted if the response shape ever changes — an absent
				// property is honest, a wrong one is not.
				trackEvent('Quote', 'Published', {
					projectId: project.companyProjectId,
					companyId: company?.id,
					revisionNumber: response?.createQuote?.project?.quote?.revisionNumber,
				});
				history.push(baselineUrl);
			},
			true,
			{file: prepared.file},
			(transaction: unknown, onFailure: (t: unknown) => unknown) => {
				// onFailure answers true when it has scheduled its own retry of this mutation —
				// the button must stay disabled through that window or a second click publishes twice.
				const retryScheduled = onFailure(transaction);
				if (retryScheduled !== true) {
					setPublishing(false);
				}
			}
		);
	}, [liveData, project, company, template, intl, history, baselineUrl]);

	const handlePublishClick = useCallback(() => {
		showPublishQuoteConfirmationModal({onConfirm: doPublish});
	}, [doPublish]);

	// Direct-URL guards: the header CTAs apply the same gates, but the URL is deep-linkable.
	// The designer is create-only — a LIVE quote is viewed as its PDF in a browser tab (the
	// Baseline header's View quote opens it), so with a live quote in place this URL just returns
	// to the Baseline. A revising quote falls through and designs revision n+1.
	if (!isQuotesEnabled() || !project || isViewMode) {
		return <Redirect to={baselineUrl} />;
	}
	if (!hasPermission(PERMISSION_TYPE.QUOTE_CREATE) || !liveData || liveData.lineItems.length === 0) {
		return <Redirect to={baselineUrl} />;
	}

	return (
		<Page>
			<PageHeader
				onBack={handleBack}
				onDownload={handleDownload}
				onPublish={handlePublishClick}
				publishDisabled={publishing}
				projectName={project.name || ''}
				projectIndicator={getProjectIndicatorString(project.companyProjectId, project.customProjectId)}
				projectColor={project.projectColor}
			/>
			{publishBlocked && (
				<ErrorBanner data-cy="quote-publish-blocked">
					{intl.formatMessage({id: 'quote_designer.error.pdf_too_large'})}
				</ErrorBanner>
			)}
			<Layout>
				{liveData && (
					<SettingsCol>
						<SettingsPanel template={template} onChange={setTemplate} mergeFields={MERGE_FIELDS} data={liveData} />
					</SettingsCol>
				)}
				<PreviewCol>{mergeContext && <PreviewPane template={template} mergeContext={mergeContext} />}</PreviewCol>
			</Layout>
		</Page>
	);
};

const QuoteDesignerPage = createFragmentContainer(QuoteDesignerPageInner, {
	viewer: graphql`
		fragment QuoteDesignerPage_viewer on Viewer @argumentDefinitions(projectId: {type: "String"}) {
			company {
				id
				name
				logoId
				currency
			}
			project(id: $projectId) {
				id
				name
				companyProjectId
				customProjectId
				projectColor
				useDeliverables
				budgetType
				baselineEstimationMinutesPerDay
				salesperson {
					id
					fullName
				}
				rateCard {
					id
					currency
				}
				client {
					id
					name
					logoId
				}
				quote {
					id
					status
					revisionNumber
				}
				phases(first: 1000000) {
					edges {
						node {
							id
							name
						}
					}
				}
				deliverables(first: 1000000) {
					edges {
						node {
							id
							name
						}
					}
				}
				phaseBaselineExpenses(first: 1000000) {
					edges {
						node {
							id
							expenseName
							expenseCategory {
								name
								id
							}
							expenseRevenue
							expenseCost
							expenseMarkup
							expenseProfit
							globalPhaseId
							deliverableId
						}
					}
				}
				phaseBaselineRoles(first: 1000000) {
					edges {
						node {
							id
							globalPhaseId
							deliverableId
							baselineMinutes
							role {
								name
								id
							}
							financialNumbers(convertToProjectCurrency: true) {
								baselineTimeAndExpenses
								baselineRevenue
								baselineCost
								baselineProfit
								baselineRatePerHour
								baselineCostPerHour
							}
						}
					}
				}
			}
		}
	`,
});

export default QuoteDesignerPage;
